import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../Assets/logo.svg';
import './AdmissionForm.css';

const INSTITUTION_ID = 40;

const AdmissionForm = () => {
    const navigate = useNavigate();

    const [formData, setFormData] = useState({});
    const [formFields, setFormFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [standards, setStandards] = useState([]);
    const [selectedStandard, setSelectedStandard] = useState('');
    const [alert, setAlert] = useState({ message: '', type: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchInstitutionData = async () => {
            try {
                const configResponse = await fetch(`https://api.sirajulhuda.com/api/v1/erp/admission-application-config/${INSTITUTION_ID}`, {
                    method: "GET",
                    headers: { "Content-Type": "application/json" },
                });

                const res = await configResponse.json();
                const institutionFormDetails = res.data;
                const institutionFormConfigs = institutionFormDetails.config;

                const fieldTypes = {
                    str: 'text', standard: 'text', school: 'text',
                    ration_card_type: 'text', yes_no: 'select',
                    local_body: 'text', int: 'number', date: 'date'
                };

                const numberFields = [
                    'mobile', 'whats_app', 'aadhaar', 'phone',
                    'father_phone', 'mother_phone', 'pin_code'
                ];

                const updatedFormFields = institutionFormConfigs
                    .filter(config =>
                        config.visibility === true &&
                        config.column_name !== 'standard' &&
                        !config.title.toLowerCase().includes('standard')
                    )
                    .map(config => ({
                        name: config.column_name,
                        title: config.title,
                        type: numberFields.includes(config.column_name) ? 'number' : fieldTypes[config.type] || 'text',
                        required: config.is_required,
                        defaultValue: config.default_value,
                        isYesNo: config.type === 'yes_no'
                    }));

                setFormFields(updatedFormFields);

                const initialFormData = {};
                updatedFormFields.forEach(field => {
                    if (field.defaultValue !== null) {
                        initialFormData[field.name] = field.isYesNo
                            ? (field.defaultValue ? "yes" : "no")
                            : field.defaultValue;
                    }
                });

                setFormData(initialFormData);
                await handleStandards();

            } catch (error) {
                console.error("Error fetching institution data:", error);
                setAlert({ message: "Error loading form. Please refresh the page.", type: 'error' });
            } finally {
                setLoading(false);
            }
        };

        fetchInstitutionData();
    }, []);

    const handleStandards = async () => {
        try {
            const response = await fetch(`https://api.sirajulhuda.com/api/v1/erp/school/standards/list/${INSTITUTION_ID}`, {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            });
            const result = await response.json();
            setStandards(result.data || []);
        } catch (error) {
            console.error("Error fetching standards:", error);
            setAlert({ message: "Error fetching standards. Please try again.", type: 'error' });
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        // Build payload dynamically from all form fields
        const payload = {
            institution_id: INSTITUTION_ID,
            standard_id: selectedStandard ? Number(selectedStandard) : null,
        };

        // Add all form fields to payload
        Object.keys(formData).forEach(key => {
            let value = formData[key];

            // Handle empty strings as null
            if (value === '' || value === undefined) {
                value = null;
            }
            // Format date fields
            else if (key === 'dob' || key === 'tc_issue_date') {
                value = value ? new Date(value).toISOString().slice(0, 19).replace('T', ' ') : null;
            }
            // Convert yes/no to boolean
            else if (value === 'yes') {
                value = true;
            }
            else if (value === 'no') {
                value = false;
            }

            payload[key] = value;
        });

        try {
            const url = 'https://api.sirajulhuda.com/api/v1/erp/admission/application/create';
            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (result.detail === "application existed") {
                setAlert({ message: 'Application already exists with this mobile number!', type: 'error' });
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                setAlert({ message: 'Application submitted successfully!', type: 'success' });
                window.scrollTo({ top: 0, behavior: 'smooth' });
                setTimeout(() => navigate('/success'), 1000);
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            setAlert({ message: 'Error submitting application. Please try again.', type: 'error' });
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="loader-container">
                <div className="loader"></div>
                <p>Loading...</p>
            </div>
        );
    }

    return (
        <div className="page">
            <div className="container">
                <div className="card">
                    {/* Header */}
                    <div className="card-header">
                        <img src={logo} alt="Logo" className="logo" />
                        <h1>Application for Admission</h1>
                        <p>Sirajul Huda School of Excellence</p>
                    </div>

                    {/* Alert */}
                    {alert.message && (
                        <div className={`alert ${alert.type}`}>
                            {alert.message}
                            <button onClick={() => setAlert({ message: '', type: '' })}>&times;</button>
                        </div>
                    )}

                    {/* Form */}
                    <form onSubmit={handleSubmit}>
                        {/* Standard */}
                        <div className="section">
                            <h2>Select Standard</h2>
                            <div className="field">
                                <label>Standard <span>*</span></label>
                                <select
                                    value={selectedStandard}
                                    onChange={(e) => setSelectedStandard(e.target.value)}
                                    required
                                >
                                    <option value="">Select Standard</option>
                                    {standards.map((std) => (
                                        <option key={std.id} value={std.id}>{std.name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Fields */}
                        <div className="section">
                            <h2>Student Details</h2>
                            <div className="fields-grid">
                                {formFields.map((field) => (
                                    <div key={field.name} className="field">
                                        <label>
                                            {field.title}
                                            {field.required && <span>*</span>}
                                        </label>
                                        {field.isYesNo ? (
                                            <select
                                                name={field.name}
                                                value={formData[field.name] || ''}
                                                onChange={handleChange}
                                                required={field.required}
                                            >
                                                <option value="">Select</option>
                                                <option value="yes">Yes</option>
                                                <option value="no">No</option>
                                            </select>
                                        ) : (
                                            <input
                                                type={field.type}
                                                name={field.name}
                                                value={formData[field.name] || ''}
                                                onChange={handleChange}
                                                required={field.required}
                                                placeholder={field.title}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Submit */}
                        <button type="submit" className="btn-submit" disabled={submitting}>
                            {submitting ? 'Submitting...' : 'Submit Application'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AdmissionForm;
